import { BCChart, BCPieChart } from '@bimser/components';
import { FormItemTypes, IDictionary, getEnumTypeKeys } from '@bimser/core';
import React from 'react';
import { IDataSource, IOnLoadArgs, IPropertyField } from '../../../../entities/common';
import { getDataSourceData, getResultData } from '../../../../helper';
import VisualControl from '../../base/VisualControl';
import { IDataLoadEventArgs } from '../../entities/EventArgs';
import {
	IAnimation,
	IArgumentAxis,
	IChartItem,
	IChartProperties,
	IHover,
	ILegend,
	ISelection,
	IStack,
	ITooltip,
	IValueAxis,
	IZoomAndPan
} from './entities';
import { EInteractions, EPositions, EZoomRegions } from './entities/enums';
import { IChartTypes } from '../../../../entities/common/IChartTypes';

const chartTypes = {
	[IChartTypes.LineSeries]: 'line',
	[IChartTypes.SplineSeries]: 'spline',
	[IChartTypes.BarSeries]: 'bar',
	[IChartTypes.ScatterSeries]: 'scatter',
	[IChartTypes.AreaSeries]: 'area'
};

export default class Chart extends VisualControl {
	data: any[];
	dataSource: IDataSource;
	charts: Array<IChartItem>;
	animation: IAnimation;
	stack: IStack;
	legend: ILegend;
	valueAxis: IValueAxis;
	argumentAxis: IArgumentAxis;
	rotate: boolean;
	selection: ISelection;
	hover: IHover;
	tooltip: ITooltip;
	zoomAndPan: IZoomAndPan;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Chart;
		this.rotate = false;
		this.caption.position = 'top';
		this.animation = { enabled: true };
		this.argumentAxis = {
			enabled: true,
			showLabels: true,
			showLine: true,
			showGrid: true,
			position: EPositions.Bottom,
			showTicks: true,
			indentFromAxis: 0,
			scaleName: '',
			tickSize: 1
		};
		this.valueAxis = {
			enabled: true,
			showGrid: true,
			showLine: true,
			showLabels: true,
			position: EPositions.Left,
			showTicks: true,
			indentFromAxis: 0,
			scaleName: '',
			tickSize: 1
		};
		this.dataSource = {
			name: '',
			valueExpr: '',
			displayExpr: [],
			displayFormat: '',
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.stack = { enabled: false };
		this.legend = { enabled: false, position: EPositions.Left };
		this.selection = { enabled: false, selectedArgument: '', selectedValue: 0 };
		this.hover = { enabled: true };
		this.tooltip = { enabled: true };
		this.zoomAndPan = { enabled: false };
		this.charts = [];

		this.__onClickSeriesItem = this.__onClickSeriesItem.bind(this);

		this.subscribe('trigger:OnClickSeriesItem', (value: string) => {
			const data = JSON.parse(value);

			const selectedChartItem = this.charts.find(i => i.name === data.series);
			const selectedData = this.data[data.point];

			this.__proxyChanging.selection.selectedValue =
				selectedData[selectedChartItem.valueField];
			this.__proxyChanging.selection.selectedArgument =
				selectedData[selectedChartItem.argumentField];
		});
	}

	__loadProperties(data: IChartProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.rotate = data.rotate;
		this.__proxyChanged.animation = data.animation;
		this.__proxyChanged.argumentAxis = data.argumentAxis;
		this.__proxyChanged.valueAxis = data.valueAxis;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.stack = data.stack;
		this.__proxyChanged.legend = data.legend;
		this.__proxyChanged.hover = data.hover;
		this.__proxyChanged.tooltip = data.tooltip;
		this.__proxyChanged.zoomAndPan = data.zoomAndPan;
		this.__proxyChanged.charts = data.charts;
		this.__proxyChanged.selection = data.selection;
		this.__proxyChanged.data = data.data;
	}

	__onLoad({ externalData, reload, forceRefresh }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.__form.serviceUrl &&
			this.__form.displayMode !== 'preview' &&
			this.dataSource?.name &&
			!this.dataSource.runAtServer
		) {
			this.__proxyChanging.loading = true;
			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				forceRefresh,
				projectVersion: this.__form.packageVersion
			})
				.then(response => {
					const data = response ? getResultData(response.data) : null;

					if (data?.length) {
						const tId = this.__form.beginTransaction();

						this.__proxyChanging.data = data;

						this.__form.endTransaction(tId).then(() => {
							this.__proxyChanging.loading = false;
							this.emit('event:OnDataLoad', { data: data } as IDataLoadEventArgs);
						});
					} else {
						this.__proxyChanging.data = [];
						this.__proxyChanging.loading = false;
					}
				})
				.catch(() => {
					this.__proxyChanging.data = [];
					this.__proxyChanging.loading = false;
				});
		}
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__dataSourceValueEntryFlag = false;
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	private getAxisData(axis: IArgumentAxis | IValueAxis) {
		return {
			...axis,
			showLabels: axis.enabled && axis.showLabels,
			showGrid: axis.enabled && axis.showGrid,
			showLine: axis.enabled && axis.showLine
		};
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...super.__getPropertyFields(),
			{
				name: 'animation',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					}
				]
			},
			{
				name: 'selection',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'selectedArgument',
						type: 'string'
					},
					{
						name: 'selectedValue',
						type: 'number'
					}
				]
			},
			{
				name: 'hover',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					}
				]
			},
			{
				name: 'tooltip',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					}
				]
			},
			{
				name: 'zoomAndPan',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'interactionWithArguments',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EInteractions).map(
							type => type as keyof typeof EInteractions
						)
					},
					{
						name: 'interactionWithValues',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EInteractions).map(
							type => type as keyof typeof EInteractions
						)
					},
					{
						name: 'zoomRegionKey',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EZoomRegions).map(
							type => type as keyof typeof EZoomRegions
						)
					}
				]
			},
			{
				name: 'stack',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					}
				]
			},
			{
				name: 'legend',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'position',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EPositions).map(
							type => type as keyof typeof EPositions
						)
					}
				]
			},
			{
				name: 'valueAxis',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'position',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EPositions).map(
							type => type as keyof typeof EPositions
						)
					},
					{
						name: 'tickSize',
						type: 'number'
					},
					{
						name: 'indentFromAxis',
						type: 'number'
					},
					{
						name: 'scaleName',
						type: 'string'
					},
					{
						name: 'showGrid',
						type: 'boolean'
					},
					{
						name: 'showTicks',
						type: 'boolean'
					},
					{
						name: 'showLine',
						type: 'boolean'
					},
					{
						name: 'showLabels',
						type: 'boolean'
					}
				]
			},
			{
				name: 'argumentAxis',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'position',
						type: 'enum',
						enumMembers: getEnumTypeKeys(EPositions).map(
							type => type as keyof typeof EPositions
						)
					},
					{
						name: 'tickSize',
						type: 'number'
					},
					{
						name: 'indentFromAxis',
						type: 'number'
					},
					{
						name: 'scaleName',
						type: 'string'
					},
					{
						name: 'showGrid',
						type: 'boolean'
					},
					{
						name: 'showTicks',
						type: 'boolean'
					},
					{
						name: 'showLine',
						type: 'boolean'
					},
					{
						name: 'showLabels',
						type: 'boolean'
					}
				]
			}
		];
	}

	__onClickSeriesItem({
		target
	}:
		| BCChart.Entities.ChartTypes.PointClickEvent
		| BCPieChart.Entities.PieChartTypes.PointClickEvent) {
		if (target && this.selection.enabled) {
			target.select();

			const item = this.charts.find(i => i.name === target.series.name);

			if (item) {
				this.__proxyChanging.selection.selectedValue = target.data[item.valueField];
				this.__proxyChanging.selection.selectedArgument = target.data[item.argumentField];
			}
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			data: this.data,
			dataSource: this.dataSource,
			charts: this.charts,
			animation: this.animation,
			stack: this.stack,
			legend: this.legend,
			valueAxis: this.valueAxis,
			argumentAxis: this.argumentAxis,
			rotate: this.rotate,
			selection: this.selection,
			hover: this.hover,
			tooltip: this.tooltip,
			zoomAndPan: this.zoomAndPan
		};
	}

	__render(children?: React.ReactNode) {
		if (children) {
			return super.__render(children);
		}

		let verticalAlignment: 'top' | 'bottom';
		let horizontalAlignment: 'left' | 'right' | 'center';

		if (this.legend.position === 'top' || this.legend.position === 'bottom') {
			verticalAlignment = this.legend.position;
			horizontalAlignment = 'center';
		} else if (this.legend.position === 'left' || this.legend.position === 'right') {
			horizontalAlignment = this.legend.position;
		}

		const argumentAxis = this.getAxisData(this.argumentAxis);
		const valueAxis = this.getAxisData(this.valueAxis);

		return super.__render(
			<BCChart.Components.Chart
				rotated={this.rotate}
				dataSource={this.data}
				title={this.title.getText()}
				style={this.style}
				onPointClick={this.__onClickSeriesItem}
			>
				{this.charts.map(chart => (
					<BCChart.Components.Series
						type={chartTypes[chart.type]}
						selectionMode="onlyPoint"
						argumentField={chart.argumentField}
						valueField={chart.valueField}
						barWidth={chart.barWidth}
						color={chart.color}
						hoverMode={!this.hover.enabled ? 'none' : undefined}
						name={chart.name}
						key={chart.name}
					/>
				))}
				<BCChart.Components.ArgumentAxis
					visible={argumentAxis.enabled}
					width={!argumentAxis.showLine ? 0 : undefined}
					position={argumentAxis.position}
				>
					<BCChart.Components.Grid visible={argumentAxis.showGrid} />
					<BCChart.Components.Label
						visible={argumentAxis.showLabels}
						indentFromAxis={argumentAxis.indentFromAxis}
						overlappingBehavior="rotate"
					/>
					<BCChart.Components.Tick
						visible={argumentAxis.showTicks}
						width={argumentAxis.tickSize}
					/>
				</BCChart.Components.ArgumentAxis>

				<BCChart.Components.ValueAxis
					visible={valueAxis.enabled}
					width={!valueAxis.showLine ? 0 : undefined}
					position={valueAxis.position}
				>
					<BCChart.Components.Grid visible={valueAxis.showGrid} />
					<BCChart.Components.Label
						visible={valueAxis.showLabels}
						indentFromAxis={valueAxis.indentFromAxis}
					/>
					<BCChart.Components.Tick
						visible={valueAxis.showTicks}
						width={valueAxis.tickSize}
					/>
				</BCChart.Components.ValueAxis>

				<BCChart.Components.Tooltip
					enabled={this.tooltip.enabled}
					zIndex={2000}
				/>

				<BCChart.Components.Animation enabled={this.animation.enabled} />

				<BCChart.Components.Legend
					visible={this.legend.enabled}
					verticalAlignment={verticalAlignment}
					horizontalAlignment={horizontalAlignment}
				/>

				{this.zoomAndPan.enabled && (
					<BCChart.Components.ZoomAndPan
						panKey={this.zoomAndPan.zoomRegionKey}
						argumentAxis={this.zoomAndPan.interactionWithArguments}
						valueAxis={this.zoomAndPan.interactionWithValues}
					/>
				)}
			</BCChart.Components.Chart>
		);
	}
}
